import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useMediaDevices } from '@/contexts/MediaDevicesContext.tsx';
import { Signals, useSignaling } from '@/contexts/SignalingContext.tsx';
import React from 'react';
import { usePeer } from '@/contexts/WebRTCContext.tsx';

type ControlsProps = {
  onToggleChat: (open?: boolean) => void;
  peerConnectionState: 'connected' | 'connecting' | 'disconnected' | 'new' | undefined;
  onSkipUser: () => void;
  sessionActive: boolean;
  handleSessionActive: (sessionActive: boolean) => void;
};

export function Controls({
  onToggleChat,
  peerConnectionState,
  onSkipUser,
  sessionActive,
  handleSessionActive
}: ControlsProps) {
  const { toggleAudio, toggleVideo, isAudioEnabled, isVideoEnabled, localStream } = useMediaDevices();
  const { isConnected, sendSignal } = useSignaling();
  // const [resetControls, setResetControls] = useState(false);

  const { setResetTrigger: setResetPeer } = usePeer();

  // Handle starting a session by sending a start signal to the server
  const handleStartSession = () => {
    if (!isConnected || !localStream) return;

    console.log('Starting session - sending start signal');
    sendSignal({
      signal: Signals.StartSession
    });
    handleSessionActive(true);
  };

  // Handle stopping a session by sending a stop signal to the server
  const handleStopSession = () => {
    if (!isConnected) return;

    console.log('Stopping session');
    sendSignal({
      signal: Signals.StopSession
    });

    handleSessionActive(false);
    setResetPeer(true);
  };

  return (
    <TooltipProvider>
      <div className="mt-4 flex justify-center items-center space-x-4">
        {/* Microphone toggle with tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={`w-12 h-12 ${isAudioEnabled ? 'bg-[#1E293B] border border-slate-700' : 'bg-red-600'} hover:bg-opacity-80 rounded-lg flex items-center justify-center shadow-lg transition-colors`}
              onClick={toggleAudio}
            >
              {isAudioEnabled ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-300"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                     strokeLinejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23"/>
                  <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/>
                  <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"/>
                  <line x1="12" y1="19" x2="12" y2="23"/>
                  <line x1="8" y1="23" x2="16" y2="23"/>
                </svg>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {isAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
          </TooltipContent>
        </Tooltip>
        {/* Video toggle with tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={`w-12 h-12 ${isVideoEnabled ? 'bg-[#1E293B] border border-slate-700' : 'bg-red-600'} hover:bg-opacity-80 rounded-lg flex items-center justify-center shadow-lg transition-colors`}
              onClick={toggleVideo}
            >
              {isVideoEnabled ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-300"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                     strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"/>
                  <rect x="1" y="5" width="15" height="14" rx="2" ry="2"/>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                     strokeLinejoin="round">
                  <line x1="1" y1="1" x2="23" y2="23"/>
                  <path d="M15 7H9a2 2 0 0 0-2 2v7.5"/>
                  <path d="M16 11l7-4v11"/>
                </svg>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
          </TooltipContent>
        </Tooltip>
        {/* Start/Stop session button with tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className={`w-12 h-12 ${sessionActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} rounded-lg flex items-center justify-center shadow-lg transition-colors`}
              onClick={sessionActive ? handleStopSession : handleStartSession}
              disabled={!localStream}
            >
              {sessionActive ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white"
                     viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                     strokeLinejoin="round">
                  <rect x="6" y="6" width="12" height="12"></rect>
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white"
                     viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                     strokeLinejoin="round">
                  <polygon points="5 3 19 12 5 21 5 3"></polygon>
                </svg>
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            {sessionActive ? 'Stop session' : 'Start session'}
          </TooltipContent>
        </Tooltip>
        {/* Skip user button with tooltip - only visible when a peer is matched and session is active */}
        {sessionActive && (peerConnectionState === 'connected') && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="w-12 h-12 bg-[#0E9BB5] hover:bg-[#0A8399] rounded-lg flex items-center justify-center shadow-lg transition-colors"
                onClick={onSkipUser}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white"
                     viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                     strokeLinejoin="round">
                  <polygon points="5 4 15 12 5 20 5 4"></polygon>
                  <line x1="19" y1="5" x2="19" y2="19"></line>
                </svg>
              </button>
            </TooltipTrigger>
            <TooltipContent>
              Skip to next person
            </TooltipContent>
          </Tooltip>
        )}
        {/* Chat toggle for all screen sizes with tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className="w-12 h-12 bg-[#1E293B] hover:bg-[#273449] rounded-lg flex items-center justify-center shadow-lg transition-colors border border-slate-700"
              onClick={() => onToggleChat()}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-slate-300"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                   strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            </button>
          </TooltipTrigger>
          <TooltipContent>
            Toggle chat
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
